import { useState, useRef, useEffect, useCallback } from 'react';
import { Play } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

interface VimeoEmbedSectionProps {
  videoId?: string;
  videoHash?: string;
}

interface VimeoVideoInfo {
  id: string;
  hash: string | null;
}

/**
 * Get Vimeo video ID and optional privacy hash from environment variables or props.
 * Supports full URLs (including unlisted URLs with hash path segment) and plain IDs.
 *
 * Unlisted Vimeo URLs look like: https://vimeo.com/1163432118/89829c34c3
 * The hash (89829c34c3) is required to embed private/unlisted videos.
 */
function getVimeoVideoInfo(videoId?: string, videoHash?: string): VimeoVideoInfo | null {
  let id: string | null = null;
  let hash: string | null = videoHash || null;

  // Try prop first
  if (videoId) {
    // Match full URL with optional hash path: vimeo.com/ID/HASH
    const urlWithHash = videoId.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)(?:\/([a-f0-9]+))?/);
    if (urlWithHash) {
      id = urlWithHash[1];
      if (urlWithHash[2] && !hash) hash = urlWithHash[2];
    } else if (/^\d+$/.test(videoId)) {
      id = videoId;
    }
  }

  // Fallback to environment variables
  if (!id) {
    const envVideoId = import.meta.env.VITE_VIMEO_VIDEO_ID;
    if (envVideoId) {
      const urlWithHash = envVideoId.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)(?:\/([a-f0-9]+))?/);
      if (urlWithHash) {
        id = urlWithHash[1];
        if (urlWithHash[2] && !hash) hash = urlWithHash[2];
      } else if (/^\d+$/.test(envVideoId)) {
        id = envVideoId;
      }
    }
  }

  if (!hash) {
    const envHash = import.meta.env.VITE_VIMEO_VIDEO_HASH;
    if (envHash) hash = envHash;
  }

  return id ? { id, hash } : null;
}

/**
 * Inline Vimeo video section displayed on the landing page.
 *
 * Uses a thumbnail-first pattern: shows a poster image with a play button
 * overlay. The Vimeo iframe is only loaded when the visitor clicks play,
 * keeping initial page weight low.
 */
export const VimeoEmbedSection = ({ videoId, videoHash }: VimeoEmbedSectionProps) => {
  const videoInfo = getVimeoVideoInfo(videoId, videoHash);
  const vimeoId = videoInfo?.id ?? null;
  const vimeoHash = videoInfo?.hash ?? null;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Build the Vimeo embed URL with optional hash for unlisted videos
  const vimeoEmbedUrl = vimeoId
    ? `https://player.vimeo.com/video/${vimeoId}?${vimeoHash ? `h=${vimeoHash}&` : ''}autoplay=1&title=0&byline=0&portrait=0`
    : null;

  // Build the oEmbed lookup URL (include hash for private videos)
  const oembedLookupUrl = vimeoId
    ? vimeoHash
      ? `https://vimeo.com/${vimeoId}/${vimeoHash}`
      : `https://vimeo.com/${vimeoId}`
    : null;

  // Fetch the Vimeo thumbnail via oEmbed (no API key needed)
  useEffect(() => {
    if (!vimeoId || !oembedLookupUrl) return;

    let cancelled = false;

    // Use Vimeo's thumbnail URL pattern as a reliable fallback
    const fallbackThumb = `https://vumbnail.com/${vimeoId}.jpg`;

    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(oembedLookupUrl)}&width=1280`)
      .then((res) => {
        if (!res.ok) throw new Error('oembed failed');
        return res.json();
      })
      .then((data) => {
        if (!cancelled && data.thumbnail_url) {
          // Request a larger thumbnail size
          const largeThumbnail = data.thumbnail_url.replace(/_\d+x\d+/, '_1280x720');
          setThumbnailUrl(largeThumbnail);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setThumbnailUrl(fallbackThumb);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [vimeoId, oembedLookupUrl]);

  const handlePlay = useCallback(() => {
    if (!vimeoId) return;
    trackEvent('vimeo_inline_play', { location: 'lander_video_section', video_id: vimeoId });
    setIsPlaying(true);
    setIsLoading(true);
    setHasError(false);
  }, [vimeoId]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Don't render the section at all if no video is configured
  if (!vimeoId) return null;

  return (
    <section
      ref={sectionRef}
      className="py-16 lg:py-24 bg-gradient-to-b from-[hsl(var(--brand-navy))] via-[hsl(var(--brand-navy)/0.85)] to-background"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 lg:mb-14">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[hsl(var(--brand-beige))] mb-4">
            Live Here at The Pearl
          </h2>
          <p className="text-[hsl(var(--brand-sky-blue))] text-lg max-w-2xl mx-auto leading-relaxed">
            Experience the lifestyle and community that awaits you at The Pearl at Grand Harbour.
          </p>
        </div>

        {/* Video Container */}
        <div className="max-w-4xl mx-auto">
          <div className="relative w-full rounded-2xl overflow-hidden shadow-luxury ring-1 ring-[hsl(var(--brand-teal)/0.2)] bg-[hsl(var(--brand-navy))]"
               style={{ paddingBottom: '56.25%' /* 16:9 */ }}
          >
            {!isPlaying ? (
              /* Thumbnail + Play Button */
              <button
                onClick={handlePlay}
                className="absolute inset-0 w-full h-full group cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-teal/50 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                aria-label="Play video: Live Here at The Pearl"
              >
                {/* Thumbnail Image */}
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt="Live Here at The Pearl - Video thumbnail"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--brand-navy))] to-[hsl(var(--brand-teal)/0.6)]" />
                )}

                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-[hsl(var(--brand-navy)/0.3)] group-hover:bg-[hsl(var(--brand-navy)/0.15)] transition-colors duration-300" />

                {/* Play Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[hsl(var(--brand-beige)/0.95)] backdrop-blur-sm flex items-center justify-center shadow-luxury group-hover:scale-110 group-hover:bg-[hsl(var(--brand-beige))] transition-all duration-300">
                    <Play className="w-8 h-8 sm:w-10 sm:h-10 text-[hsl(var(--brand-navy))] ml-1" fill="currentColor" />
                  </div>
                </div>

                {/* "Watch Video" label */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
                  <span className="text-[hsl(var(--brand-beige))] text-sm font-medium tracking-wide uppercase bg-[hsl(var(--brand-navy)/0.6)] backdrop-blur-sm px-4 py-2 rounded-full border border-[hsl(var(--brand-teal)/0.3)]">
                    Watch Video
                  </span>
                </div>
              </button>
            ) : (
              /* Vimeo Iframe (loaded on play click) */
              <>
                {/* Loading Spinner */}
                {isLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-[hsl(var(--brand-navy))]">
                    <div className="text-center">
                      <div className="w-14 h-14 border-4 border-[hsl(var(--brand-teal))] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                      <p className="text-sm text-[hsl(var(--brand-sky-blue))]">Loading video...</p>
                    </div>
                  </div>
                )}

                {/* Error State */}
                {hasError && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-[hsl(var(--brand-navy))]">
                    <div className="text-center p-8">
                      <p className="font-display text-lg font-semibold text-[hsl(var(--brand-beige))] mb-2">
                        Video Unavailable
                      </p>
                      <p className="text-sm text-[hsl(var(--brand-sky-blue))] mb-4">
                        We're sorry, but the video could not be loaded. Please try again later.
                      </p>
                    </div>
                  </div>
                )}

                <iframe
                  src={vimeoEmbedUrl!}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                  title="Live Here at The Pearl - Video"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
